import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useAppData } from '../context';
import { ROUTES } from './routePaths';
import Loader from '../components/Loader/Loader';

import {
  canViewAssociate,
  canViewError,
  canViewHome,
  canViewServiceCertification,
  canViewServicesTab,
  canViewWatchTab,
} from './routeGuards';

import { useWatchProperties } from '../hooks/useWatchProperties';

const Home = lazy(() => import('../pages/Home/Home'));
const ServiceCertification = lazy(
  () => import('../pages/ServiceCertification/ServiceCertification'),
);
const Associate = lazy(() => import('../pages/Associate/Associate'));
const AccessibilityStatement = lazy(
  () => import('../pages/AccessibilityStatement/AccessibilityStatement'),
);
const Error = lazy(() => import('../pages/Error/Error'));

function resolveAppRoute(state = {}, properties = {}) {
  const { error, association } = state;
  const { shouldEnableWatchTab } = properties;

  if (error && !association) return ROUTES.ERROR;
  if (association) return ROUTES.ASSOCIATE;
  if (!shouldEnableWatchTab) return ROUTES.SERVICES;

  return ROUTES.WATCH;
}

function AppRedirect() {
  const state = useAppData();
  const properties = useWatchProperties();

  return <Navigate to={resolveAppRoute(state, properties)} replace />;
}

export function GuardedRoute({ children, canAccess }) {
  const state = useAppData();
  const properties = useWatchProperties();

  if (!canAccess(state, properties)) {
    return <AppRedirect />;
  }

  return children;
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path={ROUTES.ROOT} element={<AppRedirect />} />

        <Route
          path={ROUTES.ERROR}
          element={
            <GuardedRoute canAccess={canViewError}>
              <Error />
            </GuardedRoute>
          }
        />

        <Route
          path={ROUTES.ASSOCIATE}
          element={
            <GuardedRoute canAccess={canViewAssociate}>
              <Associate />
            </GuardedRoute>
          }
        />

        <Route
          path={ROUTES.WATCH}
          element={
            <GuardedRoute canAccess={canViewWatchTab}>
              <Home />
            </GuardedRoute>
          }
        />

        <Route
          path={ROUTES.SERVICES}
          element={
            <GuardedRoute canAccess={canViewServicesTab}>
              <Home />
            </GuardedRoute>
          }
        />

        <Route
          path={ROUTES.SERVICE_CERTIFICATION}
          element={
            <GuardedRoute canAccess={canViewServiceCertification}>
              <ServiceCertification />
            </GuardedRoute>
          }
        />

        <Route
          path={ROUTES.ACCESSIBILITY_STATEMENT}
          element={
            <GuardedRoute canAccess={canViewHome}>
              <AccessibilityStatement />
            </GuardedRoute>
          }
        />

        <Route path="*" element={<Navigate to={ROUTES.ROOT} replace />} />
      </Routes>
    </Suspense>
  );
}
